import './main.scss'
import Sprites from '@/components/Sprites/Sprites'
import KowTitle from '@app/components/KowTitle/KowTitle'
import { useLocal } from '@/i18n/LocalContext'
import TimeStatus from '@app/components/TimeStatus/TimeStatus'
import { useStore } from '@/store'
import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import appExpose from '@app/expose'
import useRequest from '@/hooks/useRequest'
import { getItems } from '@/request'
import { checkPlayer } from '@/request/utils'
import cloneDeep from 'lodash/cloneDeep'
import { toast } from '@/utils'
import Receive from '../Receive/Receive'
import { Handler } from 'mitt'
import {
  celebrationClaim,
  celebrationShare,
  getCelebrationData,
  getPreCelebrationData,
} from '@app/request'
import glodIcon from '../../assets/static-img/gold2.png'
import integralIcon from '../../assets/static-img/integral_2.png'
import useReceive from '@app/hooks/useReceive'

const emitter = appExpose.emitter

export default function Main() {
  const { $t } = useLocal()
  const { state, setState, dispatch } = useStore()
  const playerId = state.login.userInfo.playerId
  const shareCoin = state.shareCoin
  const shareValue = state.shareValue

  const heroWidth = 1234
  const heroHeight = 1177
  const heroSrcList = useMemo(() => {
    const section = 19
    const count = 4
    const offset = 2
    const list = []
    for (let i = 1; i <= section; i++) {
      const n = count * i - 1
      list.push({
        url: `${process.env.BASE_URL_2}assets/sprites/img_webp/hero${i}_frames.png`,
        frameStart: n + 1 - count,
        frameEnd: i === section ? n - offset : n,
      })
    }
    return list
  }, [])
  const [renderHero, setRenderHero] = useState(false)
  const [heroReady, setHeroReady] = useState(false)
  const [heroAnimatePause, setHeroAnimatePause] = useState(false)

  const [rewardList, setRewardList] = useState<RewardList>([])
  const [propMap] = useRequest(
    () => {
      const idList = rewardList.map((r) => r.id)
      if (!idList.length)
        return Promise.reject<Awaited<ReturnType<typeof getItems>>>('retry')
      return getItems(Array.from(new Set(idList)))
    },
    {},
    rewardList
  )
  const rewardViewList = useMemo<RewardViewList>(() => {
    const innerPropMap = propMap == null ? {} : propMap
    return rewardList.map((item) => {
      const prop = innerPropMap[item.id]
      return {
        count: item.count,
        name: prop?.name || '',
        desc: prop?.desc || '',
        icon: prop?.icon || '',
      }
    })
  }, [rewardList, propMap])

  const totalGear = 8
  const defaultConfig = {
    userCoin: 0,
    share: true,
    /** 奖励是否已领取 */
    record: [false, false, false, false, false, false, false, false],
    /** 进度档位 1-8 */
    gear: 0,
    progress: 12,
    nextProgress: 24,
  }
  const [config, setConfig] = useState<typeof defaultConfig>({
    ...defaultConfig,
  })
  const shareIconClassName = useMemo(() => {
    if (!config.share) return 'share-icon animate'

    return 'share-icon'
  }, [config])

  const { openReceiveModal } = useReceive()

  const computeReward = useCallback(
    (share: boolean, record: boolean[], gear: number) => {
      if (!share) return true

      let count = 0
      record.forEach((v) => {
        if (v) {
          count += 1
        }
      })
      return count < gear
    },
    []
  )

  useEffect(() => {
    const _updateData = (
      baseData: Awaited<ReturnType<typeof getPreCelebrationData>>
    ) => {
      let record: boolean[] = []
      const originalRecord = baseData.record != null ? baseData.record : {}
      for (let i = 1; i <= totalGear; i++) {
        record[i - 1] = !!originalRecord[i]
      }

      dispatch({
        type: 'setBaseData',
        data: {
          startTime: baseData.startTime,
          endTime: baseData.endTime,
          playEndTime: baseData.playEndTime,
          serverTime: baseData.serverTime,
          shareCoin: baseData.shareCoin,
          shareValue: baseData.shareValue,
          isReward: computeReward(baseData.isShare, record, baseData.gear),
        },
      })

      setConfig((oldConfig) =>
        Object.assign({}, oldConfig, {
          share: baseData.isShare,
          record,
          gear: baseData.gear,
          progress: isNaN(parseFloat(baseData.progress))
            ? defaultConfig.progress
            : parseFloat(baseData.progress),
          nextProgress: isNaN(parseFloat(baseData.nextProgress))
            ? defaultConfig.nextProgress
            : parseFloat(baseData.nextProgress),
        })
      )
      setRewardList(() => {
        if (Array.isArray(baseData.reward) && !baseData.reward.length) return []

        let list: (typeof baseData.reward)[number] = []
        for (let i = 1; i <= totalGear; i++) {
          list = list.concat(baseData.reward[i])
        }
        return list
      })
    }

    const handler1: Handler = () => {
      getPreCelebrationData().then(_updateData)
    }
    const handler2: Handler = () => {
      getCelebrationData(playerId).then(_updateData)
    }
    emitter.on('getPreBaseData', handler1)
    emitter.on('getBaseData', handler2)

    const startHeroAnimate: Handler<MittEvents['selectActivity']> = (
      activity
    ) => {
      setHeroAnimatePause(activity !== 'main')
    }
    emitter.on('selectActivity', startHeroAnimate)
    return () => {
      emitter.off('getPreBaseData', handler1)
      emitter.off('getBaseData', handler2)

      emitter.off('selectActivity', startHeroAnimate)
    }
  }, [playerId])

  const [dotBottom] = useState({
    0: '0',
    1: '12',
    2: '24',
    3: '36',
    4: '48',
    5: '60',
    6: '72',
    7: '84',
    8: '96',
  })
  const lineHeight = useMemo(() => {
    const gear = config.gear
    const lineHeight =
      Number(dotBottom[gear as keyof typeof dotBottom]) +
      ([0, 8].includes(gear) ? 3.5 : 0)
    return lineHeight > 100 ? 100 : lineHeight
  }, [config, dotBottom])
  const progressStyle = useMemo(
    () => ({ height: `${lineHeight}%` }),
    [lineHeight]
  )

  const handleShare = useCallback<MouseEventHandler<HTMLDivElement>>(
    (e) => {
      const _cb = async () => {
        let res = await celebrationShare(playerId)
        if (res) {
          emitter.emit('getBaseData')
          setTimeout(() => {
            setCurrentRewardList([
              {
                count: shareCoin,
                name: $t('庆典币'),
                desc: $t(
                  '圣诞礼盒货币道具，可在圣诞礼盒活动中进行消耗刷新或购买道具'
                ),
                icon: glodIcon,
                key: 'gold',
              },
              {
                count: shareValue,
                name: $t('庆典值'),
                desc: $t('圣诞庆典档位进度值'),
                icon: integralIcon,
                key: 'integral',
              },
            ])
            setModalOpen(true)
          }, 1000)
        }
        emitter.off('share', _cb)
      }
      const _close = () => {
        emitter.off('share', _cb)
        emitter.off('hideShareList', _close)
      }
      if (!config.share) {
        emitter.on('share', _cb)
        emitter.on('hideShareList', _close)
      }
      emitter.emit('showShareList', e.nativeEvent)
    },
    [playerId, config, shareCoin, shareValue]
  )

  // 是否达到进度值且未领取奖励
  const availabilityGetReward = useCallback(
    (i: number) => !config.record[i] && i + 1 <= config.gear,
    [config]
  )

  const [modalOpen, setModalOpen] = useState(false)
  const [currentRewardList, setCurrentRewardList] = useState<RewardViewList>([])
  const pending = useRef(false)
  const getReward = useCallback(
    async (index: number) => {
      if (!(await checkPlayer('progress-reward'))) {
        return
      }
      if (availabilityGetReward(index)) {
        if (pending.current) return
        pending.current = true
        let res = await celebrationClaim(index + 1, playerId)
        pending.current = false
        if (res) {
          const newConfig = cloneDeep(config)
          newConfig.record[index] = true
          setConfig(newConfig)
          setState({
            isReward: computeReward(
              newConfig.share,
              newConfig.record,
              newConfig.gear
            ),
          })
          openReceiveModal([rewardViewList[index]])
        }
      } else {
        if (!config.record[index]) {
          toast($t('奖励未解锁'))
        } else {
          toast($t('奖励已领取'))
        }
      }
    },
    [config, rewardViewList, playerId]
  )

  useEffect(() => {
    if (renderHero) return

    setRenderHero(true)
  }, [renderHero])

  /**
   * 进度数字动画
   */
  const progressElem = useRef<HTMLElement>(null)
  useEffect(() => {
    const _animateValue = () => {
      let element = progressElem.current
      if (element == null) return

      let duration = 2000
      let start = 0
      let end = config.progress * 100
      if (end > 10000) {
        end = 10000
      }
      let startTimestamp = 0
      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp
        const progress = Math.min((timestamp - startTimestamp) / duration, 1)
        let text = (Math.floor(progress * (end - start) + start) / 100).toFixed(
          2
        )
        element.innerHTML = `${text}%`
        if (progress < 1) {
          window.requestAnimationFrame(step)
        }
      }
      window.requestAnimationFrame(step)
    }
    _animateValue()
  }, [config])

  const createDotClassName = useCallback(
    (i: number) => {
      if (config.gear >= i) return 'dot finish'

      if (config.gear < 8 && config.gear + 1 === i) return 'dot next'

      return 'dot'
    },
    [config]
  )

  const createDotStyle = useCallback(
    (i: number) => ({
      bottom: `${dotBottom[i as keyof typeof dotBottom]}%`,
    }),
    [dotBottom]
  )

  const createPropImgClassName = useCallback(
    (i: number) => (availabilityGetReward(i) ? 'animat' : ''),
    [config]
  )

  return (
    <div className="main">
      <KowTitle text={$t('圣诞庆典')} />
      <TimeStatus />
      <div className="title-desc">
        <div className="title-content">
          {$t(
            '参与活动，每日分享，获得「庆典值」助力圣诞庆典，与全球领主一同解锁奖励吧！'
          )}
        </div>
      </div>
      <div className="main-placer">
        <div className="share-info" onClick={handleShare}>
          <div className={shareIconClassName}></div>
          {!config.share ? (
            <div className="share-tooltip">
              <div className="dialog-bg"></div>
              <div className="dialog-content">
                <div>{$t('分享可得')}</div>
                <div className="share-get">
                  <div className="share-get__icon"></div>
                  <div className="share-get__count">{shareCoin}</div>
                  <div className="share-get__icon icon2"></div>
                  <div className="share-get__count">{shareValue}</div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div className="hero-box">
        {renderHero ? (
          <Sprites
            src={heroSrcList}
            corssOrigin={state.canvasCrossOrigin}
            width={heroWidth}
            height={heroHeight}
            frames={74}
            loop={true}
            autoplay={true}
            fps={20}
            pause={heroAnimatePause}
            className="hero selenium-main"
            onFirstFrame={() => setHeroReady(true)}
          />
        ) : null}
        <img
          src={`${process.env.BASE_URL}assets/img_webp/poster.png`}
          alt=" "
          className="hero"
          style={{ opacity: heroReady ? '0' : '1', position: 'relative' }}
        />
      </div>
      <div className="progress-right">
        <div className="reward-progress-block">
          <div className="reward-progress">
            <div className="line">
              <div className="progress" style={progressStyle}></div>
              {rewardViewList.map((item, i) => (
                <div
                  key={`dot_${item.name}_${item.count}_${i}`}
                  className={createDotClassName(i)}
                  style={createDotStyle(i)}
                >
                  <div className="property" onClick={() => getReward(i)}>
                    {item.icon ? (
                      <img
                        className={createPropImgClassName(i)}
                        src={item.icon}
                      />
                    ) : null}
                    {config.record[i] ? (
                      <div className="is-get">{$t('已领取')}</div>
                    ) : null}
                    {item ? (
                      <div className="property-count">{item.count}</div>
                    ) : null}
                  </div>
                  <div className="dot__text">
                    <span>{config.nextProgress}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="activity__home-progress">
          <div className="tree">
            <div className="text">{$t('当前进度')}</div>
          </div>
          <div className="progress-text">
            <i ref={progressElem}>00.00%</i>
          </div>
        </div>
      </div>
      <Receive
        open={modalOpen}
        hideText
        propList={currentRewardList}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
